import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EconomicLoopDiagram } from '../components/EconomicLoopDiagram';
import { CopyAddress } from '../components/CopyAddress';
import { HeroAtmosphere, HeroPhone } from '../components/HeroProductScene';
import { SelfOsDownloadSection } from '../components/SelfOsDownloadSection';
import { ESSENCE_CONTRACTS } from '../lib/contracts';

export default function HomePage() {
  return (
    <>
      <section className="relative isolate min-h-[100svh] overflow-hidden">
        <HeroAtmosphere />

        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[90rem] items-center px-4 pb-8 pt-[4.25rem] sm:px-6 sm:pb-10 sm:pt-[4.5rem] md:px-8 md:pb-16 md:pt-24 lg:pl-12">
          <div className="grid w-full grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)] items-center gap-2 sm:gap-4 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] md:gap-8 lg:gap-10">
            {/* Type — more column width on mobile */}
            <div className="relative z-20 min-w-0 overflow-hidden">
              <div className="pointer-events-none absolute -inset-x-2 -inset-y-3 rounded-2xl bg-gradient-to-r from-[#e8eef2]/95 via-[#e8eef2]/7 to-transparent md:hidden" aria-hidden />
              <div className="pointer-events-auto relative pr-1 sm:pr-2">
                <motion.div
                  className="essence-brand-wrap max-w-full"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="essence-brand font-display font-extrabold text-essence-ink">Essence</p>
                </motion.div>
                <motion.h1
                  className="mt-2.5 max-w-[15ch] font-display text-[0.9rem] font-semibold leading-snug text-essence-ink sm:mt-4 sm:max-w-[20rem] sm:text-lg md:text-xl"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  Stablecoin for rewards and agent payments on Base.
                </motion.h1>
                <motion.p
                  className="mt-2 max-w-[26ch] text-[0.78rem] leading-relaxed text-essence-mute sm:mt-3 sm:max-w-[22rem] sm:text-[0.95rem]"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.16 }}
                >
                  AI companies buy cheaper Resonatia inference; that spend funds Essence for nodes and agent
                  settlement.
                </motion.p>
                <motion.div
                  className="mt-4 flex flex-col gap-2 sm:mt-7 sm:flex-row sm:flex-wrap sm:gap-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.24 }}
                >
                  <Link
                    to="/how-it-works"
                    className="essence-cta inline-flex items-center justify-center rounded-lg bg-essence-teal px-4 py-2.5 text-sm font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-essence-teal-bright hover:shadow-[0_10px_24px_-12px_rgba(47,122,110,0.7)] sm:px-5 sm:py-3"
                  >
                    Read how it works
                  </Link>
                  <Link
                    to="/contracts"
                    className="essence-cta inline-flex items-center justify-center rounded-lg border border-essence-line bg-essence-panel/80 px-4 py-2.5 text-sm font-semibold text-essence-ink transition duration-200 hover:-translate-y-0.5 hover:border-essence-gold/50 sm:px-5 sm:py-3"
                  >
                    View contracts
                  </Link>
                </motion.div>
              </div>
            </div>

            {/* Phone — smaller share on mobile, still in-frame */}
            <div className="relative z-10 flex min-w-0 items-center justify-end md:justify-end md:pr-2 lg:pr-6">
              <div className="hero-phone-stage h-[min(42svh,260px)] w-auto sm:h-[min(52svh,360px)] md:h-[min(84svh,760px)]">
                <HeroPhone />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="what" className="border-t border-essence-line bg-essence-surface/70">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-gold">
            What Essence is
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-essence-ink sm:text-4xl">
            The economic layer of Self OS — starting as a stablecoin.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-essence-mute sm:text-lg">
            Essence (ESSENCE) is Self Labs / Role Model’s Base ERC-20 stablecoin and the SELF OS economic layer for
            intelligent systems. Agents can transfer and settle with it on Base. Payment for goods and services is
            a core intended use — alongside Self OS node rewards and Concierge Talk-credit conversion. It is not an
            ungated consumer mint, and phones do not print tokens.
          </p>
          <ul className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              {
                title: 'Node rewards',
                body: 'Self OS phones that contribute idle compute earn Essence on a cloud ledger first.',
              },
              {
                title: 'Agent spend',
                body: 'Pay for goods, services, and agent-to-agent settlement on Base — APIs, other agents, or merchants that accept Essence.',
              },
              {
                title: 'Treasury ↔ USDC',
                body: 'Sellers can hold Essence or exit toward USDC via the treasury deposit/withdraw rail. Convert → Talk credits is live in Concierge (capped).',
              },
            ].map((item) => (
              <li key={item.title} className="border-t border-essence-line pt-5">
                <h3 className="font-display text-lg font-semibold text-essence-ink">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-essence-mute">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="stack" className="border-t border-essence-line">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-teal">
            In SELF OS
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-essence-ink sm:text-4xl">
            Resonatia executes. Essence settles value.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-essence-mute sm:text-lg">
            From the SELF OS architecture: Aura holds memory, the Agent Avatar is the interface, Role Model AI
            coordinates agents, the Intelligence Layer routes models, Resonatia places compute, and Essence manages
            the economy — rewards, payments, and machine-speed settlement.
          </p>
          <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { n: '01', t: 'Aura', d: 'Personal memory & context' },
              { n: '02', t: 'Agent Avatar', d: 'Primary interface to intelligence' },
              { n: '03', t: 'Role Model AI', d: 'Multi-agent orchestration' },
              { n: '04', t: 'Intelligence Layer', d: 'Model routing & reasoning' },
              { n: '05', t: 'Resonatia', d: 'Distributed compute network' },
              { n: '06', t: 'Essence', d: 'Identity, rewards, payments, settlement' },
            ].map((item) => (
              <li key={item.t} className="border-t border-essence-line pt-4">
                <span className="font-display text-sm font-semibold text-essence-teal/70">{item.n}</span>
                <h3 className="mt-1 font-display text-lg font-semibold text-essence-ink">{item.t}</h3>
                <p className="mt-1 text-sm text-essence-mute">{item.d}</p>
              </li>
            ))}
          </ol>
          <Link
            to="/economy"
            className="mt-10 inline-flex text-sm font-semibold text-essence-gold hover:text-essence-gold-soft"
          >
            Economic layer overview →
          </Link>
        </div>
      </section>

      <section id="loop" className="border-t border-essence-line">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <EconomicLoopDiagram />
        </div>
      </section>

      <section id="audiences" className="border-t border-essence-line bg-essence-surface/70">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-teal">
            Who it’s for
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-essence-ink sm:text-4xl">
            Inference buyers fund the loop.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-essence-mute">
            Essence exists because AI companies need inference. Resonatia offers distributed / edge models at a
            lower cost than pure hyperscale defaults. When teams pay for API keys or hosting on Resonatia, that
            revenue is reserved for the Essence treasury — so node rewards and agent settlement share the same
            economic pool.
          </p>

          <div className="mt-12 grid gap-10 lg:grid-cols-3">
            <div>
              <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-gold">
                For AI companies
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-essence-ink">
                Cheaper inference on Resonatia.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-essence-mute sm:text-base">
                Buy API access or hosting for models via Resonatia (Stripe today). You get inference capacity; the
                spend becomes the demand side that backs Essence — not a separate tip jar.
              </p>
              <Link
                to="/how-it-works"
                className="mt-5 inline-flex text-sm font-semibold text-essence-gold hover:text-essence-gold-soft"
              >
                How spend becomes Essence →
              </Link>
            </div>
            <div>
              <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-teal">
                For AI agents
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-essence-ink">
                Pay for goods and services on Base.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-essence-mute sm:text-base">
                Transfer and settle with Essence — pay other agents, APIs, or merchants that accept it. Token
                transfers are live; a consumer marketplace is not implied.
              </p>
              <Link
                to="/for-agents"
                className="mt-5 inline-flex text-sm font-semibold text-essence-gold hover:text-essence-gold-soft"
              >
                Agent details →
              </Link>
            </div>
            <div>
              <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-teal">
                For Self OS nodes
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-essence-ink">Idle compute → Essence.</h3>
              <p className="mt-3 text-sm leading-relaxed text-essence-mute sm:text-base">
                Verified Resonatia work credits your cloud ledger. Talk-credit convert is live in Self Shell beta.
                On-chain withdraw when the treasury holds USDC.
              </p>
              <Link
                to="/for-nodes"
                className="mt-5 inline-flex text-sm font-semibold text-essence-gold hover:text-essence-gold-soft"
              >
                Node details →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SelfOsDownloadSection />

      <section id="roadmap" className="border-t border-essence-line">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-gold">
            Roadmap
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-essence-ink sm:text-4xl">Live vs coming next</h2>
          <ol className="mt-10 space-y-0">
            {[
              {
                status: 'Now',
                title: 'Cloud ledger + convert',
                body: 'Essence credited off-chain for verified Resonatia work; convert to Concierge Talk credits (capped) in Self Shell beta. On-chain transfers settle on Base.',
              },
              {
                status: 'Next',
                title: 'Funded treasury + withdraw',
                body: 'Stripe Resonatia revenue sweeps USDC into EssenceTreasury; Concierge-signed withdraw mints ESSENCE via EssenceBridge.',
              },
              {
                status: 'Later',
                title: 'Agent wallets & policy spend',
                body: 'Agent-facing wallets, metering UX, and human-set spend limits so agents can pay for goods and services inside policy.',
              },
              {
                status: 'Direction',
                title: 'Marketplace & micropayments',
                body: 'SELF Marketplace capabilities (agents, skills, models) and machine-speed pricing — streaming, per-task, per-inference. Not live as a consumer shop today.',
              },
            ].map((item, i) => (
              <li
                key={item.title}
                className="grid gap-2 border-t border-essence-line py-6 sm:grid-cols-[7rem_1fr] sm:gap-8"
              >
                <span className="font-display text-sm font-semibold uppercase tracking-wider text-essence-teal">
                  {item.status}
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold text-essence-ink">
                    <span className="text-essence-mute/60">{String(i + 1).padStart(2, '0')} — </span>
                    {item.title}
                  </h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-essence-mute sm:text-base">{item.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="contracts-preview" className="border-t border-essence-line bg-essence-surface/70">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-essence-gold">
                Contracts
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold text-essence-ink">Live on Base mainnet</h2>
              <p className="mt-3 max-w-xl text-sm text-essence-mute sm:text-base">
                Treasury may be unfunded and on-chain supply may be zero. Mint is admin/bridge-controlled — not a
                fair launch.
              </p>
            </div>
            <Link to="/contracts" className="text-sm font-semibold text-essence-teal hover:text-essence-gold">
              Full contract page →
            </Link>
          </div>
          <div className="mt-8 grid gap-4">
            {ESSENCE_CONTRACTS.map((c) => (
              <CopyAddress key={c.address} contract={c} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
